import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbActiveModal, NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { CoursesService } from 'src/app/services/courses.service';
import { Course } from 'src/app/classes/Course';
import { LessonsScheduleService } from 'src/app/services/lessons-schedule.service';
import { DatePipe } from '@angular/common';
import { DATE_FORMAT_OPTIONS, DateFormatOptions, NgbDateCustomParserFormatter } from 'src/app/helpers/NgbDateCustomParserFormatter';
import { dateToStringDate, getTime, pad } from 'src/app/helpers';

@Component({
  selector: 'app-add-edit-course-modal',
  templateUrl: './add-edit-course-modal.component.html',
  styleUrls: ['./add-edit-course-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: DATE_FORMAT_OPTIONS,
      useValue: {
        format: 'dd.mm.yyyy',
        DELIMITER: '.'
      } as DateFormatOptions
    },
    {
      provide: NgbDateParserFormatter,
      useClass: NgbDateCustomParserFormatter
    }
  ]
})
export class AddEditCourseModalComponent implements OnInit {
  @Input() course: Course;

  public errorMessage: string;
  public currentUser: any;
  public courseForm: FormGroup;
  public lessonsScheduleByDates = [];
  public selectedLessonSchedules = [];

  constructor(
    private activeModal: NgbActiveModal,
    private coursesService: CoursesService,
    private datePipe: DatePipe,
    private formBuilder: FormBuilder,
    private localStorageService: LocalStorageService,
    private scheduleService: LessonsScheduleService) {
    this.currentUser = this.localStorageService.getUser();
  }

  ngOnInit(): void {
    if (this.course) {
      let [year, month, day] = this.course.startDate.split('-').map(Number);
      const startDateStruct: NgbDateStruct = { day, month, year };
      [year, month, day] = this.course.endDate.split('-').map(Number);
      const endDateStruct: NgbDateStruct = { day, month, year };

      this.courseForm = this.formBuilder.group({
        name: [this.course.name, Validators.required],
        startDate: [startDateStruct, Validators.required],
        endDate: [endDateStruct, Validators.required],
        selectedSchedules: [[]],
      }, { validators: this.validateDateRange() });
      this.getSchedules();
    } else {
      this.courseForm = this.formBuilder.group({
        name: ['', Validators.required],
        startDate: ['', Validators.required],
        endDate: ['', Validators.required],
        selectedSchedules: [[]],
      }, { validators: this.validateDateRange() });
    }
  }

  closeModal(sendData): void {
    if (sendData instanceof FormGroup) {
      this.courseForm.markAllAsTouched();
      if (this.courseForm.invalid) {
        this.errorMessage = this.courseForm.hasError('invalidDateRange')
          ? 'Start Date cannot be later than End Date'
          : 'Please fill all required fields';
        return;
      }
      this.errorMessage = '';
      const startDate = sendData.get('startDate').value;
      const endDate = sendData.get('endDate').value;
      const sDate = `${pad(startDate.year)}-${pad(startDate.month)}-${pad(startDate.day)}`;
      const eDate = `${pad(endDate.year)}-${pad(endDate.month)}-${pad(endDate.day)}`;
      sendData.get('startDate').setValue(sDate);
      sendData.get('endDate').setValue(eDate);
    }
    this.activeModal.close(sendData);
  }

  public getSchedules(): void {
    const startDate: NgbDateStruct = this.courseForm.get('startDate').value;
    const endDate: NgbDateStruct = this.courseForm.get('endDate').value;
    const start = getTime(startDate);
    const end = getTime(endDate);
    if (start && end && start <= end) {
      const sDate = dateToStringDate(startDate);
      const eDate = dateToStringDate(endDate);
      this.scheduleService.getScheduledLessonsByDateRange(sDate, eDate).subscribe((response) => {
        this.lessonsScheduleByDates = response.map((lesson) => {
          const sD = this.datePipe.transform(lesson.startDate, 'dd.MM.yy');
          const eD = this.datePipe.transform(lesson.endDate, 'dd.MM.yy');
          return { ...lesson, label: `${lesson.name} (${sD} - ${eD})` };
        });
        this.courseForm
          .get('selectedSchedules')
          .setValue(
            this.course?.lessonSchedules
              ?.map(lesson => {
                const sD = this.datePipe.transform(lesson.startDate, 'dd.MM.yy');
                const eD = this.datePipe.transform(lesson.endDate, 'dd.MM.yy');
                return {
                  ...lesson,
                  label: `${lesson.name} (${sD} - ${eD})`
                };
              }) || []
          );
      });
    }
  }

  public compareById = (a: any, b: any) => a && b && a.id === b.id;

  toggleDateTimeState($event): void {
    $event.stopPropagation();
  }

  private validateDateRange(): ValidatorFn {
    return (form: AbstractControl): ValidationErrors | null => {
      const startDate = form.get('startDate')?.value;
      const endDate = form.get('endDate')?.value;
      if (!startDate || !endDate || !startDate.year || !endDate.year) {
        return null;
      }
      const start = getTime(startDate);
      const end = getTime(endDate);

      if (!start || !end) {
        return null;
      }

      return start <= end ? null : { invalidDateRange: true };
    };
  }
}
